import React, { useEffect, useMemo } from 'react';
import { Alert, AlertIcon, AlertTitle, Flex, Text } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { choiceSeparator } from 'studio_react/questionnaire/common';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import { ButtonProps } from 'ts/common/components/gallery';
import { type QuestionnaireError } from 'ts/common/components/questionnaire/Questionnaire';
import QuestionnaireQuestionField from 'ts/common/components/questionnaire/QuestionnaireQuestionField';
import { useTranslate } from 'ts/common/hooks';

/**
 * Form values keyed by page index, then question id (string keys for react-hook-form path inference).
 * Text/dropdown/radio: string; checkboxes: string[] (choice text values).
 */
export type QuestionnaireFormValues = {
    pages: Record<string, Record<string, string | string[]>>;
};

function buildPageDefaultValues(
    questionnairePage: SpApi.Client.IQuestionnairePage,
    pageIndex: number
): QuestionnaireFormValues {
    const pageKey = String(pageIndex);
    const pageValues: Record<string, string | string[]> = {};

    questionnairePage.questions.forEach((question) => {
        const questionKey = String(question.id);

        if (question.responseType === 'choices_checkboxes') {
            pageValues[questionKey] = question.responseText?.split(choiceSeparator) ?? [];
        } else {
            pageValues[questionKey] = question.responseText ?? '';
        }
    });

    return { pages: { [pageKey]: pageValues } };
}

interface Props {
    questionnairePage: SpApi.Client.IQuestionnairePage;
    pageIndex: number;
    totalPages: number;
    setSearchParams: (params: { page?: string }) => void;
    onSubmit: () => void;
    saveQuestionResponse: (questionId: number, responseText: string | null) => Promise<void>;
    submitting: boolean;
    savingFailed: boolean;
    error: Nullable<QuestionnaireError>;
}

const QuestionnairePageForm: React.FC<Props> = ({
    questionnairePage,
    pageIndex,
    totalPages,
    setSearchParams,
    onSubmit,
    saveQuestionResponse,
    submitting,
    savingFailed,
    error
}) => {
    const t = useTranslate('clientQuestionnaire');

    const defaultValues = useMemo(
        () => buildPageDefaultValues(questionnairePage, pageIndex),
        [questionnairePage, pageIndex]
    );

    const formMethods = useForm<QuestionnaireFormValues>({
        defaultValues
    });

    const { handleSubmit, reset } = formMethods;

    useEffect(() => {
        reset(buildPageDefaultValues(questionnairePage, pageIndex));
    }, [questionnairePage, pageIndex, reset]);

    const buttons: Array<ButtonProps> = [
        ...(pageIndex > 0
            ? [
                  {
                      text: t('previousPage'),
                      variant: 'outline primary' as const,
                      onClick: () => setSearchParams({ page: String(pageIndex) })
                  }
              ]
            : []),
        ...(pageIndex < totalPages - 1
            ? [
                  {
                      text: t('nextPage'),
                      variant: 'primary' as const,
                      onClick: () => {
                          handleSubmit(() => {
                              setSearchParams({ page: String(pageIndex + 2) });
                          })();
                      }
                  }
              ]
            : []),
        ...(pageIndex === totalPages - 1
            ? [
                  {
                      text: submitting ? t('saving') : t('submit'),
                      variant: 'primary' as const,
                      onClick: handleSubmit(onSubmit),
                      disabled: submitting
                  }
              ]
            : [])
    ];

    return (
        <FormProvider {...formMethods}>
            <Flex flexDirection="column" marginY="16px" gap="16px">
                <Text as="h3" fontSize="18px" fontWeight="600">
                    {questionnairePage.name}
                </Text>
                {[...questionnairePage.questions]
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((question) => (
                        <QuestionnaireQuestionField
                            key={question.id}
                            question={question}
                            pageIndex={pageIndex}
                            saveQuestionResponse={saveQuestionResponse}
                        />
                    ))}
            </Flex>
            {savingFailed && (
                <Alert status="error">
                    <AlertIcon />
                    <AlertTitle>{error?.detail ?? t('errorSavingQuestionnaire')}</AlertTitle>
                </Alert>
            )}
            <ResponsiveButtonGroup buttons={buttons} />
        </FormProvider>
    );
};

export default QuestionnairePageForm;
